import React, { useEffect, useMemo, useState } from 'react';
import apiService from '../services/api';

/**
 * Sélecteur catégorie / sous-catégorie CatalogueRubrique (réutilisé Produit & Prestation).
 */
export default function CatalogueRubriqueSelect({
  commercantId,
  categorie = '',
  sousCategorie = '',
  onChange,
  disabled = false,
}) {
  const [catalogueTree, setCatalogueTree] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showNewCategoryInput, setShowNewCategoryInput] = useState(false);
  const [showNewSubCategoryInput, setShowNewSubCategoryInput] = useState(false);
  const [existingCategories, setExistingCategories] = useState([]);

  useEffect(() => {
    if (!commercantId) return undefined;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const data = await apiService.getCatalogueRubriques(commercantId);
        const categories = Array.isArray(data?.categories) ? data.categories : [];
        if (cancelled) return;
        setCatalogueTree(categories);
        setExistingCategories(
          categories.map((c) => c.nom).filter((n) => n && n.trim())
        );
      } catch {
        if (!cancelled) {
          setCatalogueTree([]);
          setExistingCategories([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [commercantId]);

  const selectedCategoryNode = useMemo(
    () => catalogueTree.find((c) => c.nom === categorie),
    [catalogueTree, categorie]
  );

  const subOptions = useMemo(
    () =>
      (selectedCategoryNode?.sousCategories || [])
        .map((s) => s.nom)
        .filter((n) => n && n.trim()),
    [selectedCategoryNode]
  );

  const ensureCatalogueRubrique = async (niveau, nom, parentId = null) => {
    if (!commercantId || !nom?.trim()) return;
    await apiService.createCatalogueRubrique(commercantId, {
      niveau,
      nom: nom.trim(),
      parentId,
    });
    const data = await apiService.getCatalogueRubriques(commercantId);
    const categories = Array.isArray(data?.categories) ? data.categories : [];
    setCatalogueTree(categories);
    setExistingCategories(categories.map((c) => c.nom).filter((n) => n && n.trim()));
  };

  const emit = (next) => {
    if (typeof onChange === 'function') onChange(next);
  };

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
      <div>
        <label className="mb-1 block text-sm font-medium text-gray-700">Catégorie</label>
        {!showNewCategoryInput ? (
          <select
            value={categorie || ''}
            disabled={disabled || loading}
            onChange={(e) => {
              const value = e.target.value;
              if (value === '__new__') {
                setShowNewCategoryInput(true);
                emit({ categorie: '', sousCategorie: '' });
              } else {
                setShowNewSubCategoryInput(false);
                emit({ categorie: value, sousCategorie: '' });
              }
            }}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200"
          >
            <option value="">-- Sélectionner une catégorie --</option>
            {existingCategories.map((cat) => (
              <option key={cat} value={cat}>
                {cat}
              </option>
            ))}
            <option value="__new__">+ Ajouter une nouvelle catégorie</option>
          </select>
        ) : (
          <div className="flex gap-2">
            <input
              type="text"
              value={categorie || ''}
              disabled={disabled}
              onChange={(e) => emit({ categorie: e.target.value, sousCategorie: '' })}
              className="flex-1 rounded-lg border border-gray-300 px-3 py-2"
              placeholder="Nouvelle catégorie"
              autoFocus
            />
            <button
              type="button"
              className="rounded-lg bg-indigo-600 px-3 py-2 text-white"
              onClick={async () => {
                const nom = String(categorie || '').trim();
                if (nom) await ensureCatalogueRubrique(1, nom);
                setShowNewCategoryInput(false);
              }}
            >
              ✓
            </button>
            <button
              type="button"
              className="rounded-lg border border-gray-300 px-3 py-2"
              onClick={() => {
                setShowNewCategoryInput(false);
                emit({ categorie: '', sousCategorie: '' });
              }}
            >
              ✕
            </button>
          </div>
        )}
      </div>

      <div>
        <label className="mb-1 block text-sm font-medium text-gray-700">Sous-catégorie</label>
        {!showNewSubCategoryInput ? (
          <select
            value={sousCategorie || ''}
            disabled={disabled || loading || !categorie}
            onChange={(e) => {
              const value = e.target.value;
              if (value === '__new__') {
                setShowNewSubCategoryInput(true);
                emit({ categorie, sousCategorie: '' });
              } else {
                emit({ categorie, sousCategorie: value });
              }
            }}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200"
          >
            <option value="">-- Optionnel --</option>
            {subOptions.map((sub) => (
              <option key={sub} value={sub}>
                {sub}
              </option>
            ))}
            {categorie ? (
              <option value="__new__">+ Ajouter une sous-catégorie</option>
            ) : null}
          </select>
        ) : (
          <div className="flex gap-2">
            <input
              type="text"
              value={sousCategorie || ''}
              disabled={disabled || !categorie}
              onChange={(e) => emit({ categorie, sousCategorie: e.target.value })}
              className="flex-1 rounded-lg border border-gray-300 px-3 py-2"
              placeholder="Nouvelle sous-catégorie"
              autoFocus
            />
            <button
              type="button"
              className="rounded-lg bg-indigo-600 px-3 py-2 text-white"
              onClick={async () => {
                const nom = String(sousCategorie || '').trim();
                if (nom && selectedCategoryNode?.id) {
                  await ensureCatalogueRubrique(2, nom, selectedCategoryNode.id);
                }
                setShowNewSubCategoryInput(false);
              }}
            >
              ✓
            </button>
            <button
              type="button"
              className="rounded-lg border border-gray-300 px-3 py-2"
              onClick={() => {
                setShowNewSubCategoryInput(false);
                emit({ categorie, sousCategorie: '' });
              }}
            >
              ✕
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
